import { useEffect, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Legend, Line, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, euro, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const meseLbl = (k) => `${MESI[Number(k.slice(5, 7)) - 1]} ${k.slice(2, 4)}`;
const eur0 = (v) => `${Math.round(v)} €`;
const tip = (v) => (typeof v === "number" ? euro(v) : v);

const Box = ({ title, sub, children, testId, wide }) => (
  <div className={`rounded-lg border border-slate-200 bg-white p-5 shadow-sm ${wide ? "xl:col-span-2" : ""}`} data-testid={testId}>
    <h3 className="font-heading text-base font-bold text-slate-900">{title}</h3>
    {sub && <p className="mb-3 text-xs text-slate-500">{sub}</p>}
    <div className="h-64">{children}</div>
  </div>
);

const Vuoto = () => <p className="flex h-full items-center justify-center text-sm text-slate-400">Ancora nessun dato</p>;

const EsportaBilancio = () => {
  const ora = new Date().getFullYear();
  const [anno, setAnno] = useState(String(ora));
  const [busy, setBusy] = useState(false);
  const scarica = async () => {
    setBusy(true);
    try {
      const r = await api.get("/statistiche/excel", { params: { anno }, responseType: "blob" });
      const nome = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || `Bilancio_${anno}.xlsx`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = nome; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="esporta-bilancio">
      <Select value={anno} onValueChange={setAnno}>
        <SelectTrigger className="h-9 w-24 bg-white" data-testid="esporta-bilancio-anno"><SelectValue /></SelectTrigger>
        <SelectContent>{[0, 1, 2].map((k) => <SelectItem key={k} value={String(ora - k)}>{ora - k}</SelectItem>)}</SelectContent>
      </Select>
      <Button variant="outline" onClick={scarica} disabled={busy} className="h-9 border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid="esporta-bilancio-button">
        <FileSpreadsheet className="mr-1 h-4 w-4" /> {busy ? "Preparazione..." : "Scarica bilancio Excel"}
      </Button>
    </div>
  );
};

const Riepilogo = ({ m }) => (
  <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="guadagno-riepilogo">
    {[["Incassi del mese", m.incassi, "incassi"], ["Costo pezzi venduti", -m.costi_acquisto, "costi"], ["Spese extra", -m.spese, "spese"], ["Guadagno effettivo", m.guadagno, "guadagno"]].map(([l, v, k]) => (
      <div key={k} className={`rounded-lg border p-4 ${k === "guadagno" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`}>
        <p className={`text-[11px] font-bold uppercase tracking-wider ${k === "guadagno" ? "text-orange-400" : "text-slate-500"}`}>{l}</p>
        <p className={`mt-2 font-mono text-2xl font-bold ${k === "guadagno" && v < 0 ? "text-rose-400" : ""}`} data-testid={`riepilogo-${k}`}>{euro(v)}</p>
      </div>
    ))}
  </div>
);

const ArticoliChart = ({ data, valore, nome, colore }) => (data.length === 0 ? <Vuoto /> : (
  <ResponsiveContainer><BarChart data={data} layout="vertical" margin={{ left: 10, right: 16 }}>
    <CartesianGrid strokeDasharray="3 3" horizontal={false} /><XAxis type="number" fontSize={11} tickFormatter={valore === "utile" ? eur0 : undefined} />
    <YAxis type="category" dataKey="nome" width={120} fontSize={11} tickFormatter={(s) => (s.length > 18 ? `${s.slice(0, 17)}…` : s)} />
    <Tooltip formatter={valore === "utile" ? tip : undefined} /><Bar dataKey={valore} name={nome} fill={colore} radius={[0, 3, 3, 0]} />
  </BarChart></ResponsiveContainer>
));

const GRAFICI = {
  vendite_mese: (s, mesi) => (
    <Box key="v" title="Andamento vendite mensile" sub="Banco, officina e noleggi, con il guadagno effettivo" testId="grafico-vendite-mese" wide>
      <ResponsiveContainer><ComposedChart data={mesi}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="lbl" fontSize={11} /><YAxis fontSize={11} tickFormatter={eur0} /><Tooltip formatter={tip} /><Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="vendite_banco" name="Banco" stackId="a" fill="#ea580c" /><Bar dataKey="officina" name="Officina" stackId="a" fill="#0f172a" /><Bar dataKey="noleggi" name="Noleggi" stackId="a" fill="#94a3b8" />
        <Line dataKey="guadagno" name="Guadagno" stroke="#059669" strokeWidth={2.5} dot={false} /></ComposedChart></ResponsiveContainer>
    </Box>
  ),
  riparazioni_mese: (s, mesi) => (
    <Box key="r" title="Andamento riparazioni mensile" sub="Schede di lavoro consegnate" testId="grafico-riparazioni-mese">
      <ResponsiveContainer><BarChart data={mesi}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="lbl" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="riparazioni" name="Riparazioni" fill="#0f172a" radius={[3, 3, 0, 0]} /></BarChart></ResponsiveContainer>
    </Box>
  ),
  ore_lavoro: (s, mesi) => (
    <Box key="o" title="Ore di lavoro incassate" sub={`Ultimi 12 mesi: ${s.totali.ore} ore · ${euro(s.totali.manodopera)} di manodopera`} testId="grafico-ore-lavoro">
      <ResponsiveContainer><ComposedChart data={mesi}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="lbl" fontSize={11} /><YAxis yAxisId="o" fontSize={11} /><YAxis yAxisId="e" orientation="right" fontSize={11} tickFormatter={eur0} /><Tooltip formatter={(v, n) => (n === "Ore" ? v : euro(v))} /><Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar yAxisId="o" dataKey="ore" name="Ore" fill="#ea580c" radius={[3, 3, 0, 0]} /><Line yAxisId="e" dataKey="manodopera" name="Manodopera €" stroke="#0f172a" strokeWidth={2} dot={false} /></ComposedChart></ResponsiveContainer>
    </Box>
  ),
  prodotti_top: (s) => (
    <Box key="p" title="Prodotti più venduti" sub="Pezzi venduti, banco e officina, ultimi 12 mesi" testId="grafico-prodotti-top">
      <ArticoliChart data={s.prodotti_top} valore="quantita" nome="Pezzi" colore="#ea580c" />
    </Box>
  ),
  utili_prodotti: (s) => (
    <Box key="u" title="Utili dei prodotti" sub="Prezzo di vendita meno prezzo di acquisto, ultimi 12 mesi" testId="grafico-utili-prodotti">
      <ArticoliChart data={s.utili_prodotti} valore="utile" nome="Utile" colore="#059669" />
    </Box>
  ),
};

export const DashboardGrafici = ({ scelti }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/statistiche").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const mesi = s.mesi.map((m) => ({ ...m, lbl: meseLbl(m.mese) }));
  return (
    <section className="mb-8" data-testid="dashboard-grafici">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-base font-bold text-slate-900">Conti del mese</h2>
        <EsportaBilancio />
      </div>
      <Riepilogo m={s.mesi[s.mesi.length - 1]} />
      {scelti.length > 0 && <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">{scelti.map((k) => GRAFICI[k]?.(s, mesi))}</div>}
    </section>
  );
};
